import { ToggleGroup } from "@kobalte/core/toggle-group"
import { assignInlineVars } from "@vanilla-extract/dynamic"
import { createMemo, createSignal, For, Show } from "solid-js"
import { Box } from "@/designSystem/components/box"
import { LinkButton } from "@/designSystem/components/button"
import { Icon } from "@/designSystem/components/icon"
import {
  buttonToggleClass,
  buttonToggleGroupClass,
} from "@/designSystem/components/mini/mini.css"
import { Spinner } from "@/designSystem/components/spinner"
import { Text } from "@/designSystem/components/text"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  formatDimensionsClass,
  formatMetaClass,
  previewAspectRatio,
  previewBlockerClass,
  previewImageClass,
  previewLoadingClass,
  previewWrapperClass,
} from "./social-image-generator.css"

interface ImageFormat {
  name: string
  label: string
  platforms: string
  aspectRatio: string
  width: number
  height: number
}

const FORMATS: ImageFormat[] = [
  {
    name: "portrait",
    label: "Portrait",
    platforms: "Instagram · Facebook · LinkedIn · X",
    aspectRatio: "4:5",
    width: 1080,
    height: 1350,
  },
  {
    name: "square",
    label: "Square",
    platforms: "Instagram · Facebook · X",
    aspectRatio: "1:1",
    width: 1080,
    height: 1080,
  },
  {
    name: "landscape",
    label: "Landscape",
    platforms: "Facebook · LinkedIn · X",
    aspectRatio: "16:9",
    width: 1200,
    height: 630,
  },
  {
    name: "story",
    label: "Story",
    platforms: "Instagram · Facebook · TikTok",
    aspectRatio: "9:16",
    width: 1080,
    height: 1920,
  },
]

export function SocialMediaImageGenerator(props: {
  socialImageUrls: Record<string, string>
}) {
  const { LL } = useI18nContext()
  const [selectedIndex, setSelectedIndex] = createSignal(0)
  const [isLoading, setIsLoading] = createSignal(true)

  const selectedFormat = () => FORMATS[selectedIndex()] ?? FORMATS[0]!

  const previewUrl = createMemo((prev: string | undefined) => {
    const url = props.socialImageUrls[selectedFormat().name] ?? ""
    if (url !== prev) setIsLoading(true)
    return url
  })

  return (
    <Box gap="16">
      <Box gap="4">
        <Text fontSize="m" color="sand800" fontWeight="600">
          {LL().postShare.socialImages()}
        </Text>
        <Text fontSize="xs" color="sand600">
          {LL().postShare.socialImagesDescription()}
        </Text>
      </Box>

      <ToggleGroup
        class={buttonToggleGroupClass}
        value={String(selectedIndex())}
        onChange={v => setSelectedIndex(Number(v))}
      >
        <For each={FORMATS}>
          {(format, i) => (
            <ToggleGroup.Item
              class={buttonToggleClass}
              value={String(i())}
              aria-label={format.label}
              data-selected={selectedIndex() === i()}
            >
              {format.label}
            </ToggleGroup.Item>
          )}
        </For>
      </ToggleGroup>

      <div class={previewWrapperClass}>
        <Show when={isLoading()}>
          <div class={previewLoadingClass}>
            <Spinner size="medium" />
          </div>
        </Show>
        <img
          src={previewUrl()}
          alt=""
          class={previewImageClass}
          style={assignInlineVars({
            [previewAspectRatio]: `${selectedFormat().width} / ${selectedFormat().height}`,
          })}
          onLoad={() => setIsLoading(false)}
        />
        <div class={previewBlockerClass} />
      </div>

      <div class={formatMetaClass}>
        <span class={formatDimensionsClass}>
          {selectedFormat().width} × {selectedFormat().height}
        </span>
        <LinkButton
          kind="surface"
          hue="sand"
          size="s"
          href={previewUrl()}
          download={`social-${selectedFormat().name}.png`}
          title={LL().postShare.download()}
        >
          <Icon name="download-cloud" size="14" color="sand700" />
          {LL().postShare.download()}
        </LinkButton>
      </div>
    </Box>
  )
}
